import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { 
  UsersIcon, 
  CalendarDaysIcon, 
  XCircleIcon,
  CurrencyEuroIcon 
} from '@heroicons/react/24/outline';
import { estadisticasApi } from '../../services/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { StatisticsPage, StatisticsConfig } from './StatisticsPage';
import { StatisticsSelector } from '../../components/dashboard/StatisticsSelector';
import { TodayAppointments } from '../../components/dashboard/TodayAppointments';
import type {
  EstadisticaCitasPorMes,
  EstadisticaTasaCancelacion,
  IngresosPorTratamiento
} from '../../types/estadisticas';

export function DashboardPage() {
  const { t } = useTranslation();
  
  // Estado para la configuración de estadísticas
  const [statisticsConfig, setStatisticsConfig] = useState<StatisticsConfig>({
    citasPorMes: true,
    cancelacionesPorMes: true,
    topTratamientos: true,
    rankingProfesionales: false,
    distribucionGenero: false,
    distribucionEdad: false,
    ingresosTratamiento: false,
    citasVsCancelaciones: false,
    horasPico: false
  });

  // Obtener la fecha de hoy en formato YYYY-MM-DD
  const today = new Date().toISOString().slice(0, 10);

  // Consultas para los KPIs principales
  const { data: pacientesActivos, isLoading: loadingPacientes } = useQuery({
    queryKey: ['estadisticas', 'pacientes-activos'],
    queryFn: estadisticasApi.getPacientesActivos,
  });

  const { data: citasHoy = [], isLoading: loadingCitasHoy } = useQuery<any[]>({
    queryKey: ['estadisticas', 'citas-hoy', today],
    queryFn: () => estadisticasApi.getCitasAgrupadas({ fecha_inicio: today, fecha_fin: today, group_by: 'day' }),
  });

  const { data: cancelacionesHoy = [], isLoading: loadingCancelacionesHoy } = useQuery<any[]>({
    queryKey: ['estadisticas', 'cancelaciones-hoy', today],
    queryFn: () => estadisticasApi.getCancelacionesAgrupadas({ fecha_inicio: today, fecha_fin: today, group_by: 'day' }),
  });

  const { data: tasaCancelacionHoy = [], isLoading: loadingTasaHoy } = useQuery<any[]>({
    queryKey: ['estadisticas', 'tasa-cancelacion-hoy', today],
    queryFn: () => estadisticasApi.getTasaCancelacionAgrupada({ fecha_inicio: today, fecha_fin: today, group_by: 'day' }),
  });

  const { data: ingresosHoy = [], isLoading: loadingIngresosHoy } = useQuery<any[]>({
    queryKey: ['estadisticas', 'ingresos-hoy', today],
    queryFn: () => estadisticasApi.getIngresosPorTratamientoAgrupados({ fecha_inicio: today, fecha_fin: today, group_by: 'day' }),
  });

  // KPIs del día actual
  const totalCitasHoy = citasHoy.length > 0 ? (citasHoy[0].total_citas || citasHoy[0].total || 0) : 0;
  const totalCancelacionesHoy = cancelacionesHoy.length > 0 ? (cancelacionesHoy[0].total_cancelaciones || cancelacionesHoy[0].total || 0) : 0;
  const tasaCancelacionActual = tasaCancelacionHoy.length > 0 ? (tasaCancelacionHoy[0].tasa_cancelacion || tasaCancelacionHoy[0].tasa || 0) : 0;
  const ingresosTotalesHoy = ingresosHoy.length > 0 ? ingresosHoy.reduce((acc, item) => acc + (item.ingreso_estimado || item.ingresos || 0), 0) : 0;

  // Estado de carga global para estadísticas
  const isLoadingGlobal = loadingPacientes || loadingCitasHoy || loadingCancelacionesHoy || loadingTasaHoy || loadingIngresosHoy;

  if (isLoadingGlobal) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('dashboard.loadingStats')} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('dashboard.title')}
        </h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t('dashboard.welcomeMessage')}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        <KPICard
          title={t('dashboard.totalActivePatients')}
          value={String(pacientesActivos?.total ?? 0)}
          icon={UsersIcon}
          color="blue"
        />
        <KPICard
          title="Citas Hoy"
          value={String(totalCitasHoy ?? 0)}
          icon={CalendarDaysIcon}
          color="green"
        />
        <KPICard
          title="Tasa de Cancelación (Hoy)"
          value={typeof tasaCancelacionActual === 'number' && !isNaN(tasaCancelacionActual) ? `${tasaCancelacionActual.toFixed(1)}%` : '0%'}
          icon={XCircleIcon}
          color="red"
        />
        <KPICard
          title="Ingresos Hoy"
          value={`€${Number(ingresosTotalesHoy).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={CurrencyEuroIcon}
          color="yellow"
        />
      </div>

      {/* Sección de citas de hoy */}
      <div>
        <TodayAppointments />
      </div>

      {/* Selector de estadísticas y título de sección */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Análisis Detallado
          </h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
            Personaliza las estadísticas que deseas visualizar
          </p>
        </div>
        <div className="flex-shrink-0 w-full lg:w-auto">
          <StatisticsSelector 
            config={statisticsConfig}
            onChange={setStatisticsConfig}
          />
        </div>
      </div>

      {/* Incluir página de estadísticas */}
      <div className="mt-6">
        <StatisticsPage 
          config={statisticsConfig}
          showHeader={false}
          isLoading={isLoadingGlobal}
        />
      </div>
    </div>
  );
}

// Componente para las tarjetas KPI
function KPICard({ 
  title, 
  value, 
  icon: Icon, 
  color 
}: { 
  title: string; 
  value: string; 
  icon: React.ComponentType<any>; 
  color: 'blue' | 'green' | 'red' | 'yellow';
}) {
  const colorClasses = {
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900',
    green: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900',
    red: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900',
    yellow: 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900',
  };

  return (
    <div className="card">
      <div className="flex items-center">
        <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-3 sm:ml-4 min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate mb-1">
            {title}
          </p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
} 